import { aliasStatus, answerRefEligibility, hasRecallChip, hasVariableChip, TRIG_CALL_PATTERN } from '@agavi/blocks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { evaluateFormula, type AngleMode } from './formula-eval';
import { useDelayedStickyError } from './use-settled-value';

import type { PickerHandle } from '@/components/task/flow-picker-menu';
import type { TaskVariable } from '@/types/experience';

import { aliasShortName, answerRefAddress, answerRefDefaultName, STATUS_CLASS, useAnswerRefScope } from '@/components/task/answer-ref-context';
import { FormulaInsertMenu } from '@/components/task/formula-insert-menu';
import { RecallRenamePopover } from '@/components/task/recall-rename-popover';
import { useRecallAffordance } from '@/components/task/use-recall-affordance';
import { useVariableAffordance } from '@/components/task/use-variable-affordance';
import { useAngleMode } from '@/contexts/angle-mode-context';


// ── Constants ─────────────────────────────────────────────────────────────────

// Zero-width space: invisible text node that lets the browser place a caret
// immediately after a contenteditable="false" chip span. Stripped from the
// serialised formula so it never reaches the evaluator.
const ZWS = '\u200B';


// ── DOM helpers ───────────────────────────────────────────────────────────────

/**
 * A RECALLED ANSWER IN A FORMULA, which is a chip like a variable's and a token unlike it.
 *
 * A variable is written into the formula BY NAME, because its name is its identity. An alias's
 * is not: the name is derived from the source block and the teacher may rename it, so a formula
 * holding `{the velocity}` would go stale the moment either changed — the exact sweep the alias
 * model exists to abolish. So the token carries the ID and the chip renders the live name.
 *
 * SQUARE BRACKETS, not braces. Not a new signifier: `[` already opens the recall picker, the
 * toolbar button's icon is brackets, and `AnswerRefChip.renderText` already writes `[name]` when
 * a chip lands somewhere that cannot hold one. Braces would also collide with the rule below
 * that a typed `{x}` becomes a chip for a KNOWN VARIABLE — an alias id is not one.
 */
/**
 * EVERY KIND OF CHIP, in one place.
 *
 * A formula holds two now — a variable, written by name, and a recalled answer, written by id —
 * and every behaviour this field gives a chip has to reach both. It did not: eight call sites
 * asked for `[data-formula-var]`, so a recall chip got no one-press delete, no drag, no keyboard
 * reorder and no click handling. The visible symptom was Backspace needing two presses, because
 * the first press ate the invisible anchor the helpers exist to skip.
 *
 * One selector and one predicate, so the next behaviour cannot be added for one kind only.
 */
const CHIP_SELECTOR = '[data-formula-var],[data-formula-ref]';
const isChipEl = (n: unknown): n is HTMLElement =>
  n instanceof HTMLElement && (n.dataset.formulaVar != null || n.dataset.formulaRef != null);

function makeRefChip(aliasId: string, label: string, address?: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('data-formula-ref', aliasId);
  /*
   * NO WIDTH CAP. There was one — 14ch with an ellipsis — while the equation shared its line
   * with a tolerance box and a chip could fill what was left. It read badly: the name was cut
   * mid-word with no way to see the rest but hovering. The equation has the whole line now, so
   * the cap has nothing left to protect against, and the field's own `overflow-x-auto` handles
   * a name long enough to run past it.
   */
  span.setAttribute('class', 'ws-answer-ref-chip');
  span.setAttribute('tabindex', '0');
  span.setAttribute('role', 'button');
  // The short label is on screen; the full address is one hover away — see `aliasShortName`.
  if (address) span.setAttribute('title', address);
  span.textContent = label;
  return span;
}

function makeChip(name: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('data-formula-var', name);
  span.setAttribute('class', 'ws-var-chip ws-var-chip-draggable');
  span.setAttribute('tabindex', '0');
  span.setAttribute('role', 'button');
  span.textContent = `{${name}}`;
  return span;
}

/**
 * The formula either side of the caret (or of the selection), as two strings.
 *
 * Pasting has to rebuild the WHOLE field rather than splice text into it, because a pasted run can
 * contain several `{name}`s and only `populate` turns all of them into chips. So the field is
 * taken apart into "what is before", the pasted text, and "what is after", and put back together
 * through the one function that knows how to build chips.
 */
function serializeAround(el: HTMLElement, range: Range): [string, string] {
  const box = (fragment: DocumentFragment) => {
    const holder = document.createElement('div');
    holder.appendChild(fragment);
    return serialize(holder);
  };
  const head = range.cloneRange();
  head.selectNodeContents(el);
  head.setEnd(range.startContainer, range.startOffset);
  const tail = range.cloneRange();
  tail.selectNodeContents(el);
  tail.setStart(range.endContainer, range.endOffset);
  return [box(head.cloneContents()), box(tail.cloneContents())];
}

/**
 * A private-use character, parked in the formula string to mark where the caret should end up and
 * removed the moment the field has been rebuilt around it.
 *
 * Carrying the position as a NUMBER instead would mean counting rendered characters past chips and
 * zero-width spacers to find it again, which is the sort of arithmetic that is right until a chip
 * sits on the boundary. A character rides along with the text and cannot be off by one.
 */
const CARET_MARK = '\uE000';

function placeCaretAtMark(el: HTMLElement): void {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const text = node as Text;
    const at = (text.textContent ?? '').indexOf(CARET_MARK);
    if (at !== -1) {
      text.textContent = (text.textContent ?? '').replace(CARET_MARK, '');
      const range = document.createRange();
      range.setStart(text, at);
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      return;
    }
    node = walker.nextNode();
  }
}

/**
 * A formula field is the ONE place braces are typing syntax rather than literal text.
 *
 * Everywhere else in the app a variable exists only as a chip chosen from the variables menu, and
 * typed `{x}` stays the characters `{x}`. Here that rule would cost more than it buys: a formula is
 * a maths expression, parentheses do the grouping, and braces have no other possible meaning — so
 * typing `{x}` can only be an attempt to reference a variable.
 *
 * Two things keep it honest. The conversion happens the MOMENT the closing brace is typed, in front
 * of the teacher, rather than silently on some later repopulate — which is how this behaved by
 * accident before, and why a typo could quietly become a chip days later. And it only fires for a
 * name that actually exists, so a chip still cannot denote a variable the menu could not have
 * offered; an unknown name simply stays as text, visibly not a chip.
 */
const KNOWN = (variables: { name: string }[]): Set<string> => new Set(variables.map(v => v.name));

// Walk child nodes → formula string.
// Text nodes → their text (ZWS stripped); chip spans → {name}.
function serialize(el: HTMLElement): string {
  let out = '';
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      out += (node.textContent ?? '').replace(/\u200B/g, '');
    } else if (node instanceof HTMLElement) {
      const v = node.dataset.formulaVar;
      const r = node.dataset.formulaRef;
      if (v != null) out += `{${v}}`;
      // The ID, never the name on screen — see `makeRefChip`.
      else if (r != null) out += `[${r}]`;
      else if (node.tagName !== 'BR') out += node.textContent ?? '';
    }
  }
  return out;
}

/**
 * The same walk, but written for a HUMAN rather than for storage.
 *
 * A reference is stored as `[ara_017b178f-…]`, which is what the clipboard was handing over —
 * so copying an equation and pasting it anywhere gave a line of uuids. Reported exactly that
 * way. Outside Agavi that text is all a reader gets; inside it, Keyword and Fill Blank accept
 * no chips, so the plain flavour is what lands there too, and a uuid is not an answer anybody
 * can read.
 *
 * The chip's own visible text, because that is already the short name a teacher chose or the
 * derived one standing in for it — the brackets are what keep it from reading as a word, the
 * same bargain `renderText` strikes for the rich-text chip.
 */
function serializeReadable(el: HTMLElement): string {
  let out = '';
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      out += (node.textContent ?? '').replace(/\u200b/g, '');
    } else if (node instanceof HTMLElement) {
      const v = node.dataset.formulaVar;
      if (v != null) out += `{${v}}`;
      else if (node.dataset.formulaRef != null) out += `[${node.textContent ?? ''}]`;
      else if (node.tagName !== 'BR') out += node.textContent ?? '';
    }
  }
  return out;
}

/** The attribute our own copies carry the canonical formula in. See the copy handler. */
const FORMULA_CLIPBOARD_ATTR = 'data-agavi-formula';

/*
 * Parse a formula string → populate editor DOM.
 *
 * Every chip gets a text node on BOTH sides, because a caret can only sit in a text node and a
 * chip is `contenteditable="false"`. The trailing one was always here. The leading one was not,
 * and only mattered for a chip with nothing before it — which is to say a formula that STARTS
 * with a variable, like `{river}+{kayak}`. There was nowhere to put the caret in front of it, by
 * click or by arrow key, so that formula could not be edited at the front at all.
 *
 * The teacher who reported it was trying to wrap one in a negation. They could not get in front
 * of the chip, and the formula they ended up saving was `{-{river}-{kayak})*{time}` — an opening
 * `{` where `(` was meant, which parses as nothing and silently fails to grade.
 */
function populate(
  el: HTMLElement, formula: string, known: Set<string>,
  /** An alias's live short name and its full address, or undefined once it is gone — see
   *  `makeRefChip` and `aliasShortName`. */
  refName?: (aliasId: string) => { label: string; address?: string } | undefined,
): void {
  el.innerHTML = '';
  // ONE SCAN over both token kinds, so a formula mixing them cannot have its chips built in two
  // passes that disagree about where the plain text between them starts.
  const re = /\{([^}]+)\}|\[([^\]]+)\]/g;
  let last = 0;
  let m;
  while ((m = re.exec(formula)) !== null) {
    // An unknown name renders as the literal text it is. A chip that points at a variable which no
    // longer exists is a lie the teacher cannot see through; plain braces show the reference broke.
    // The same for a recall whose alias is gone, and for the same reason.
    const named = m[1] !== undefined
      ? (known.has(m[1]) ? { label: m[1] } : undefined)
      : refName?.(m[2]!);
    if (named === undefined) continue;
    if (m.index > last) el.appendChild(document.createTextNode(formula.slice(last, m.index)));
    // Nothing before this chip to hold a caret: two chips in a row already have the trailing ZWS
    // of the first between them, so in practice this is the chip that opens the formula.
    else if (!el.lastChild || el.lastChild.nodeType !== Node.TEXT_NODE) {
      el.appendChild(document.createTextNode(ZWS));
    }
    el.appendChild(m[1] !== undefined
      ? makeChip(m[1])
      : makeRefChip(m[2]!, named.label, named.address));
    el.appendChild(document.createTextNode(ZWS));
    last = re.lastIndex;
  }
  if (last < formula.length) el.appendChild(document.createTextNode(formula.slice(last)));
  // Deliberately left with NO children when the formula is empty, rather than seeded with an empty
  // text node. That node was the one thing distinguishing a pristine field from one that had been
  // typed into and cleared: the browser never rebuilds it, so the caret rendered at the right edge
  // until the first keystroke, went left, and stayed left until a remount ran this again. An empty
  // contenteditable is a shape browsers handle natively — they insert their own <br> on focus, and
  // `serialize` already skips BR precisely because it expects to meet one.
}

/**
 * Turns a just-typed `{name}` immediately before the caret into a real chip.
 *
 * Local surgery rather than a repopulate: rebuilding the whole field would lose the caret and
 * flicker on every keystroke. Splitting the one text node keeps everything else untouched and lets
 * the caret land after the new chip, where the teacher was about to keep typing.
 *
 * Returns whether anything changed, so the caller only re-serialises when it did.
 */
function autoChipBeforeCaret(el: HTMLElement, known: Set<string>): boolean {
  const sel = window.getSelection();
  if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return false;
  const node = sel.anchorNode;
  if (!(node instanceof Text) || !el.contains(node)) return false;

  const text = node.textContent ?? '';
  const caret = sel.anchorOffset;
  // Only the text ENDING at the caret: typing `}` elsewhere in the line must not reach back and
  // convert something the teacher had deliberately left alone.
  const m = text.slice(0, caret).match(/\{([^{}]+)\}$/);
  if (!m || !known.has(m[1]!)) return false;

  const start = caret - m[0].length;
  const tail = document.createTextNode(text.slice(caret));
  const zws = document.createTextNode(ZWS);
  const chip = makeChip(m[1]!);
  // ZWS rather than an empty string when the chip lands at the very start, so there is still a
  // text node in front of it to put a caret in. Same reason as the leading ZWS in `populate`.
  node.textContent = text.slice(0, start) || ZWS;
  node.after(chip, zws, tail);

  const range = document.createRange();
  range.setStart(zws, zws.length);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
}

// Insert a zero-width sentinel and measure its bounding rect to find where the
// caret is on screen. Synchronous: DOM is restored before this returns.
function getCaretRect(
  range: Range,
  editorEl: HTMLElement,
): { left: number; top: number; height: number } | null {
  if (!editorEl.contains(range.startContainer)) return null;
  // Don't try to insert inside a contenteditable="false" chip
  const isInsideChip = (n: Node) =>
    (n.nodeType === Node.ELEMENT_NODE ? (n as HTMLElement) : n.parentElement)
      ?.closest(CHIP_SELECTOR) != null;
  if (isInsideChip(range.startContainer)) return null;
  const marker = document.createElement('span');
  marker.textContent = ZWS;
  try {
    range.insertNode(marker);
    const r = marker.getBoundingClientRect();
    return { left: r.left, top: r.top, height: r.height || 18 };
  } catch {
    return null;
  } finally {
    marker.remove();
  }
}

// When the mouse hovers over a chip, snap the caret range to before/after
// the chip depending on which half was hit.
function resolveRange(raw: Range, clientX: number): Range {
  const chip = (raw.startContainer.nodeType === Node.ELEMENT_NODE
    ? (raw.startContainer as HTMLElement)
    : raw.startContainer.parentElement
  )?.closest<HTMLElement>(CHIP_SELECTOR);
  if (!chip) return raw;
  const { left, width } = chip.getBoundingClientRect();
  const r = document.createRange();
  if (clientX < left + width / 2) r.setStartBefore(chip);
  else r.setStartAfter(chip);
  r.collapse(true);
  return r;
}

function caretRangeAt(x: number, y: number): Range | null {
  if (document.caretRangeFromPoint) return document.caretRangeFromPoint(x, y);
  const pos = (document as any).caretPositionFromPoint?.(x, y);
  if (!pos) return null;
  const r = document.createRange();
  r.setStart(pos.offsetNode, pos.offset);
  r.collapse(true);
  return r;
}

// ── Drag state ────────────────────────────────────────────────────────────────

interface DragState {
  chip: HTMLSpanElement;

  ghost: HTMLDivElement;
  indicator: HTMLDivElement;
  isDragging: boolean;
  startX: number;
  startY: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface FormulaFieldProps {
  /** Names the data key or item this field writes, so an undo can point at it. Applied to BOTH
   * render branches — the no-variables one and the full one — since which of them a block gets
   * depends on the task, not on the field. */
  fieldId?: string;
  /**
   * The key this field's ANGLE MODE is stored under, when it is not its own `fieldId`.
   *
   * For a pair that is one quantity in two boxes — a range trigger's low and high, a
   * between-answer's bounds. They must share a mode, because two bounds of the same measurement
   * disagreeing about degrees is never what anyone meant, but they keep separate `fieldId`s so
   * undo can still point at whichever one actually changed.
   */
  angleModeKey?: string;
  value: string;
  onChange: (val: string) => void;
  variables: TaskVariable[];
  rolledValues: Record<string, string>;
  ariaLabel: string;
  placeholder?: string;
  hasError?: boolean;
  /** Hide the '= result' evaluated preview below the input. */
  hideEval?: boolean;
  /**
   * NO RECALLED ANSWERS IN THIS FIELD — for the variables panel, and anywhere else a formula
   * is evaluated before a student has answered anything.
   *
   * A task variable is rolled when the task STARTS. An answer recall is a number a student
   * produced in an earlier task, which may not exist yet and certainly does not during the
   * roll — so a variable defined in terms of one has no value at the moment every block that
   * reads it needs one. It is not a narrower menu, it is a different lifetime.
   *
   * Deliberately a flag on this component and not a second component. The field is the same
   * field: same chips, same parsing, same keyboard. What differs is one capability, and a
   * fork would have to be kept in step with this one forever.
   */
  noAnswerRefs?: boolean;
  /**
   * A mode supplied BY HAND, which is now only how a block's old block-wide setting reaches a
   * field that has not been set individually.
   *
   * It is deliberately NOT defaulted here: `useAngleMode` has to be able to tell "the caller
   * said degrees" from "the caller said nothing", or a block's fallback could never apply. The
   * resolved default is degrees — what a teacher writing `sin(30)` means, and NOT mathjs's own
   * default, which is radians.
   */
  angleMode?: AngleMode;
  /**
   * Makes the angle mode THIS FIELD'S OWN, with a toggle inside the field.
   *
   * Passed, the field shows a DEG/RAD button beside the variables picker whenever its own text
   * calls a trig function, and pressing it reports the other mode. Omitted, `angleMode` is read
   * only — for a field that inherits one, and for the read-only renderings.
   *
   * PER FIELD, not per block, which is the whole point. A block-level switch cannot express a
   * radian feedback beside a degree answer, cannot reach a branch condition, and cannot reach a
   * VARIABLE at all: a variable belongs to no block, and one task may want some of its equations
   * in degrees and others in radians.
   */
  onAngleModeChange?: (mode: AngleMode) => void;
  /** Outermost div classes — use for flex/width, e.g. 'min-w-0 flex-1' */
  className?: string;
  onBlur?: () => void;
  /** Fires when the equation editor itself gains focus — callers wired into the shared
   * rich-text toolbar (useSharedEditorToolbar) should use this to null out the active editor,
   * since this contentEditable is a separate, non-Tiptap surface the toolbar can never actually
   * target. Without it, the toolbar keeps pointing at whatever Tiptap editor was last focused
   * and stays lit up instead of ghosting out, inviting its variable-insert button to silently
   * land in the wrong field. */
  onFocus?: () => void;
  /**
   * Put the caret here on mount. Used when the field appears as the direct result of an action
   * that requires filling it in — ticking "Allow alternative answer setups" mounts the branch
   * carousel, and the equation is the one thing that must be supplied next.
   *
   * Deliberately not RichTextEditor's `autoFocus` prop: that trips jsx-a11y/no-autofocus, which
   * every block editor was moved off earlier. A mount effect does the same job without it.
   */
  focusOnMount?: boolean;
  /** A unit pinned inside the field, e.g. Compass's degree sign. Rendered as a flex item rather
   * than an absolute overlay inside the field — this row is already a flex container, so the suffix takes
   * its own space instead of needing padding reserved for it, and can never sit over the value.
   *
   * `aria-hidden`, the same reasoning the old UnitSuffix overlay records: announced here it would arrive after the
   * value, detached from it. The unit belongs in `ariaLabel`, where a screen reader meets it in the
   * right order — so a caller passing `suffix` should name the unit in the label too. */
  suffix?: React.ReactNode;
  /** Id of an element describing this field — e.g. Compass's cardinal-direction hint, which sits
   * beside the input and is meaningless to a screen reader unless it is associated. A plain
   * <input> took `aria-describedby` directly; this component has to forward it. */
  ariaDescribedBy?: string;
}

export function FormulaField({
  value, onChange, variables, rolledValues, fieldId, angleModeKey,
  ariaLabel, placeholder, hasError, hideEval = false, noAnswerRefs = false, angleMode, className = '', onBlur, onFocus,
  ariaDescribedBy, suffix, focusOnMount, onAngleModeChange,
}: FormulaFieldProps) {
  const { t } = useTranslation();
  const editorRef      = useRef<HTMLDivElement>(null);
  const lastValueRef   = useRef(value);
  const dragRef        = useRef<DragState | null>(null);
  // Stable ref to latest onChange so mouse event listeners don't go stale
  const onChangeRef  = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  // Same reason as onChangeRef: the drag listeners are attached to the document once and would
  // otherwise close over the variable list as it stood when the drag began.
  const variablesRef = useRef(variables);
  useEffect(() => { variablesRef.current = variables; }, [variables]);
  // Same reason as `variablesRef`: the drag/drop path repopulates from a listener that closed
  // over its own render, so it has to read the current resolver rather than that render's.
  /** The chip labels the field was last drawn with — see `refSignature`. */
  const lastRefSignatureRef = useRef<string>('');
  const refNameRef = useRef<(aliasId: string) => { label: string; address?: string } | undefined>(
    () => undefined);

  /**
   * WHERE THE CARET WAS, for a tool that has to take focus to work.
   *
   * The variables picker is a real menu with a search box, so by the time it reports a choice the
   * selection is long gone. Recording it as it moves is the only way to put a chip where the
   * teacher was typing rather than at the start of the field.
   */
  const caretRef = useRef<Range | null>(null);
  useEffect(() => {
    function remember() {
      const el = editorRef.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0) return;
      const r = sel.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) caretRef.current = r.cloneRange();
    }
    document.addEventListener('selectionchange', remember);
    return () => document.removeEventListener('selectionchange', remember);
  }, []);

  useEffect(() => {
    if (focusOnMount) editorRef.current?.focus();
    // Mount only — a later flip of the flag must not steal the caret back mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * A formula is invalid for most of the time it takes to write one — `{n}*2+` is malformed at
   * every keystroke until the last. Reporting that as you type is a field interrupting you to say
   * you have not finished yet, so the error is held back while the caret is in the box and lands
   * the moment you leave it. The value itself is not gated: everything downstream (evaluation,
   * issue list, the block's own validation) still sees the truth immediately. Only the red border
   * and aria-invalid wait, and only for the field actually being edited.
   */
  /**
   * One rule for every caller, applied here rather than at 34 call sites: delay the FIRST
   * appearance until the value stops changing, then hold it until the fault is gone.
   *
   * Owning it here is what makes it uniform, and callers must NOT also gate `hasError` on their
   * own settle flag. A caller-side gate does not merely double the delay, it defeats the sticky
   * half: it drives `hasError` false while the teacher types, which is indistinguishable from the
   * fault being fixed, so the marker blinks off and the delay restarts on every keystroke. Caught
   * by polling the border at 50ms while typing — it went dark for ~750ms mid-word. `hasError` must
   * mean "is this value faulty right now", nothing more; when it is shown is decided here.
   */
  const showError = useDelayedStickyError(!!hasError, value);

  const namedVars  = variables.filter(v => v.name);
  /*
   * WHETHER THIS FIELD MAY TAKE A VARIABLE — grandfathered on its own contents.
   *
   * A field already holding `{dip} * 2` reads as allowed however the capability stands, because
   * the alternative is the plain-input branch showing those braces as literal text, and a
   * `{name}` is a chip or a value and never literal.
   *
   * The BRANCH below is deliberately left alone. It drops the whole insert menu, recall included,
   * so forcing it here would take Answer Recall away with Variables — a different capability. It
   * also needs no help for the case it was built for: a teacher with variables off has none
   * authored, `namedVars` is empty, and the plain input is already what every formula field
   * renders. Only the affordances are withheld.
   */
  const varsAllowed = useVariableAffordance() === 'allowed';
  const insertableVars = varsAllowed ? namedVars : [];
  /*
   * THE FIELD'S OWN MODE, resolved here rather than at 44 call sites.
   *
   * `useAngleMode` prefers what this field has stored, falls back to whatever the caller passed
   * by hand (a block's old block-wide setting), then to degrees. `setMode` exists whenever a
   * block is providing the scope and this field has a `fieldId`; `onAngleModeChange` is the
   * explicit route, for a field whose owner is not a block at all — a variable.
   */
  /*
   * THE RECALL SCOPE, or none outside a task.
   *
   * Absent on every surface that is not a block editor — the same gate the rich-text picker
   * uses — and absent is what hides the button: a formula with nowhere to pull an answer from
   * should not offer to.
   */
  const [renaming, setRenaming] = useState<{ aliasId: string; anchor: DOMRect } | null>(null);
  const scopeFromContext = useAnswerRefScope();
  /* `noAnswerRefs` is applied HERE, at the single place the scope enters this component, so
     every consumer of it — the button, the `[` shortcut, chip rendering, `insertRef` — is
     covered by one line rather than each remembering to ask. */
  /* The capability joins `noAnswerRefs` here rather than beside it, because this one line is
     what every consumer reads — the button, the `[` shortcut, chip rendering, `insertRef`. A
     block above the rung therefore withholds the recall half exactly as a field that never had
     one does, and the glyph closes to `{}` with no second rule. */
  const recallAllowed = useRecallAffordance() === 'allowed';
  const refsOff = noAnswerRefs || !recallAllowed;
  const refScope = refsOff ? null : scopeFromContext;
  /** An alias's live name for its chip, and `undefined` once the alias is gone. */
  const refName = useCallback((aliasId: string) => {
    const alias = refScope?.aliases.find(a => a.id === aliasId);
    if (!alias) return undefined;
    const first = alias.members[0];
    /*
     * A NUMBER, OR IT IS NOT A CHIP HERE.
     *
     * The insert menu already offers only `kind: 'number'` facets, so this cannot be reached
     * by authoring — it is reached by PASTE, which rebuilds a chip from any `[ara_…]` it
     * finds, and that door was open. A word pasted into an equation became a chip that
     * resolved to nothing, so the formula quietly yielded no value.
     *
     * Returning undefined is not a new rule: `populate` already renders an unresolvable
     * reference as the literal text it is, for a variable that no longer exists and for an
     * alias that is gone, on the grounds that plain braces show the reference broke where a
     * chip would hide it. A text recall in an equation is broken in exactly that sense.
     */
    const target = first && refScope?.tasks
      .find(tk => tk.id === first.taskId)?.blocks?.find(b => b.id === first.blockId);
    if (target) {
      const eligibility = answerRefEligibility(
        target.type, (target.data ?? {}) as Record<string, unknown>);
      const facet = eligibility.referenceable
        ? eligibility.facets.find(f => f.id === first!.facet)
        : undefined;
      if (facet?.kind !== 'number') return undefined;
    }
    return {
      label: aliasShortName(alias, refScope!, t, 'number'),
      address: first ? answerRefAddress(refScope!, first, t) : undefined,
    };
  }, [refScope, t]);

  useEffect(() => { refNameRef.current = refName; }, [refName]);

  const { mode: effectiveAngleMode, setMode } = useAngleMode(angleModeKey ?? fieldId, angleMode);
  const changeAngleMode = onAngleModeChange ?? setMode;
  const evalResult = value.trim() ? evaluateFormula(value, rolledValues, effectiveAngleMode) : null;
  // No focus-within highlight: this is a text-entry surface and the caret shows focus, same rule
  // the plain inputs and rich-text editors follow (see globals.css).
  const borderCls  = showError ? 'border-ws-error-fg' : 'border-ws-border';

  // ── Initialise / sync ───────────────────────────────────────────────────────

  useEffect(() => {
    if (editorRef.current) populate(editorRef.current, value, KNOWN(variables), refName);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * WHAT THE RECALL CHIPS CURRENTLY READ AS — because the formula string cannot say.
   *
   * A recall is stored as `[aliasId]` and its NAME is derived at render, which is what keeps
   * it following a rename for free. The sync below guarded on the formula string alone, so a
   * rename changed nothing it could see: `refName` was already in the dep array and the
   * effect re-ran, then returned on its first line. Reported as "i updated the alias of the
   * recall chip, but the chip hasn't updated ... it still has its original name".
   *
   * A signature rather than a redraw on every render: `populate` rebuilds the field's DOM, so
   * running it unconditionally would move the caret on every keystroke, which is the thing
   * the guard is there to prevent.
   */
  const refSignature = useMemo(
    () => [...value.matchAll(/\[([^\]]+)\]/g)]
      .map(m => `${m[1]}\u0001${refName(m[1]!)?.label ?? ''}`)
      .join('\u0000'),
    [value, refName],
  );

  useEffect(() => {
    if (value === lastValueRef.current && refSignature === lastRefSignatureRef.current) return;
    lastValueRef.current = value;
    lastRefSignatureRef.current = refSignature;
    if (editorRef.current) populate(editorRef.current, value, KNOWN(variables), refName);
  }, [value, variables, refName, refSignature]);

  // ── Drag document listeners ─────────────────────────────────────────────────

  useEffect(() => {
    function onMove(e: MouseEvent) {
      const drag = dragRef.current;
      if (!drag) return;

      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (!drag.isDragging && Math.sqrt(dx * dx + dy * dy) < 5) return;

      drag.isDragging = true;
      drag.chip.style.opacity = '0.3';
      // Now it is a drag, so the ghost earns its place on screen — see `handleEditorMouseDown`.
      drag.ghost.style.display = '';

      // Move ghost with cursor
      drag.ghost.style.left = `${e.clientX + 10}px`;
      drag.ghost.style.top  = `${e.clientY - 12}px`;

      // Show drop indicator
      const el = editorRef.current;
      if (!el) return;
      const raw = caretRangeAt(e.clientX, e.clientY);
      if (raw) {
        const resolved = resolveRange(raw, e.clientX);
        const rect = getCaretRect(resolved, el);
        if (rect) {
          drag.indicator.style.display = 'block';
          drag.indicator.style.left    = `${rect.left - 1}px`;
          drag.indicator.style.top     = `${rect.top}px`;
          drag.indicator.style.height  = `${rect.height}px`;
          return;
        }
      }
      drag.indicator.style.display = 'none';
    }

    function onUp(e: MouseEvent) {
      const drag = dragRef.current;
      if (!drag) return;

      drag.ghost.remove();
      drag.indicator.remove();
      drag.chip.style.opacity = '';
      dragRef.current = null;

      /*
       * A PLAIN CLICK ON A CHIP PUTS THE CARET BESIDE IT, on the side that was clicked.
       *
       * `handleEditorMouseDown` calls `preventDefault()` on every chip mousedown so the chip can
       * be dragged, which also stops the browser placing a caret. For a chip in the middle of a
       * formula that costs nothing — there is text either side to click instead. For a formula
       * that OPENS with one there is no text in front at all, only the zero-width spacer, which
       * has no pixels to aim at.
       *
       * Reported exactly that way: `{lum}/(4*pi*{radius}^2))^0.25` was missing its opening
       * bracket and there was nowhere to click to type it. Left half puts the caret before,
       * right half after, which is what clicking a word does everywhere else.
       */
      if (!drag.isDragging) {
        /*
         * A RECALL CHIP OPENS ITS NAME, where a variable chip places the caret.
         *
         * Asked for in those words: inside an equation a chip is most of the line, because a
         * reference's default name is its whole address — so this is the surface where a
         * short name is worth the most, and it was the only one with no way to set one.
         *
         * Only a recall. A variable chip has no alias to name, and it is also the common
         * opener of a formula: the left/right caret placement below exists because a formula
         * STARTING with a chip has nothing in front of it to click, and taking that from
         * variables to give recalls a menu would trade one gap for another.
         *
         * The threshold above is what makes this safe to hang on a click at all — five pixels
         * of travel is a drag, so a press that moves never opens this.
         */
        const aliasId = drag.chip.dataset.formulaRef;
        if (aliasId) {
          setRenaming({ aliasId, anchor: drag.chip.getBoundingClientRect() });
          return;
        }
        const sel = window.getSelection();
        if (!sel) return;
        // FOCUS FIRST. Focusing a contenteditable moves the caret to its start, so doing it
        // after would undo the placement below — which is exactly what happened.
        editorRef.current?.focus();
        const box = drag.chip.getBoundingClientRect();
        const range = document.createRange();
        if (e.clientX < box.left + box.width / 2) range.setStartBefore(drag.chip);
        else range.setStartAfter(drag.chip);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        return;
      }

      const el = editorRef.current;
      if (!el) return;

      const raw = caretRangeAt(e.clientX, e.clientY);
      if (!raw || !el.contains(raw.startContainer)) return;

      const dropRange = resolveRange(raw, e.clientX);
      // Don't drop inside the chip itself
      if (drag.chip.contains(dropRange.startContainer)) return;

      // Remove the chip and its trailing ZWS anchor from the current position
      const nextSib = drag.chip.nextSibling;
      drag.chip.remove();
      if (nextSib?.nodeType === Node.TEXT_NODE && nextSib.textContent === ZWS) {
        nextSib.remove();
      }

      /*
       * THE SAME ELEMENT, not a rebuilt one.
       *
       * This was `makeChip(drag.varName)`, which builds a VARIABLE chip from a name — and a
       * recall chip has no name to build from. `chip.dataset.formulaVar` is undefined on one,
       * the `!` at the capture site hid that, and the drop produced a chip whose
       * `data-formula-var` was the string "undefined", which `serialize` then wrote into the
       * equation as `{undefined}`. Reported as an undefined error on moving a recall chip
       * inside an equation.
       *
       * Re-inserting the node that was lifted keeps every attribute of whichever kind it is,
       * and removes the asymmetry rather than adding a second branch that the next chip kind
       * would have to be remembered in too. A removed node is still a node.
       */
      const newChip = drag.chip;
      const anchor  = document.createTextNode(ZWS);
      dropRange.insertNode(anchor);
      dropRange.insertNode(newChip);

      requestAnimationFrame(() => {
        const sel = window.getSelection();
        if (!sel) return;
        const r = document.createRange();
        r.setStart(anchor, anchor.length);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
      });

      const s = serialize(el);
      lastValueRef.current = s;
      onChangeRef.current(s);
    }

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
      dragRef.current?.ghost.remove();
      dragRef.current?.indicator.remove();
    };
  }, []); // stable — uses refs only

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleInput() {
    if (!editorRef.current) return;
    // Convert before serialising, so the value that leaves here already describes the chip rather
    // than the braces that produced it.
    autoChipBeforeCaret(editorRef.current, KNOWN(variables));
    const s = serialize(editorRef.current);
    lastValueRef.current = s;
    onChange(s);
  }

  function removeChip(chip: HTMLSpanElement): void {
    const el = editorRef.current;
    if (!el) return;
    const next = chip.nextSibling;
    // Where the chip sat, captured BEFORE removing it: this is where the caret belongs afterwards.
    const parent: Node = chip.parentNode ?? el;
    const caretIndex = Array.prototype.indexOf.call(parent.childNodes, chip);
    chip.remove();
    if (next?.nodeType === Node.TEXT_NODE && next.textContent === ZWS) next.remove();
    const s = serialize(el);
    lastValueRef.current = s;
    onChange(s);

    /**
     * Leave a plain caret in the gap, not focus on a neighbouring chip.
     *
     * This used to do `remaining[idx] ?? remaining[idx - 1]` and `.focus()` it, which made deleting
     * a chip SELECT another one. In `{test}+{test2}`, removing `{test2}` jumped the highlight onto
     * `{test}`, so the next Backspace ate that chip instead of the `+` sitting between them — the
     * caret skipped over a character the teacher could see.
     *
     * Safe to set synchronously: `removeChip` mutates the DOM in place rather than repopulating,
     * and the value-sync effect bails when `value === lastValueRef.current`, which was just
     * assigned above. So nothing rebuilds this subtree underneath the range.
     */
    el.focus();
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.setStart(parent, Math.min(caretIndex, parent.childNodes.length));
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  // A chip's ZWS anchor always sits immediately after it (never before) — so a caret resting
  // anywhere in/around that anchor reads on screen as "right after the chip," but by default the
  // browser treats the anchor as ordinary text and eats it on the first Backspace, leaving the
  // chip untouched and requiring a second press. These two helpers resolve the caret's actual
  // collapsed position to the chip it's touching (if any), skipping over that invisible anchor,
  // so Backspace/Delete can remove the chip itself in one press instead of the anchor first.
  function chipBeforeCaret(el: HTMLElement): HTMLSpanElement | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
    const { startContainer, startOffset } = sel.getRangeAt(0);
    if (!el.contains(startContainer)) return null;

    let prev: ChildNode | null;
    if (startContainer.nodeType === Node.TEXT_NODE) {
      if (startContainer.textContent === ZWS || startOffset === 0) prev = startContainer.previousSibling;
      else return null;
    } else {
      prev = startContainer.childNodes[startOffset - 1] ?? null;
    }
    if (prev?.nodeType === Node.TEXT_NODE && prev.textContent === ZWS) prev = prev.previousSibling;
    return isChipEl(prev) ? (prev as HTMLSpanElement) : null;
  }

  function chipAfterCaret(el: HTMLElement): HTMLSpanElement | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
    const { startContainer, startOffset } = sel.getRangeAt(0);
    if (!el.contains(startContainer)) return null;

    let next: ChildNode | null;
    if (startContainer.nodeType === Node.TEXT_NODE) {
      if (startOffset === (startContainer.textContent?.length ?? 0)) next = startContainer.nextSibling;
      else return null;
    } else {
      next = startContainer.childNodes[startOffset] ?? null;
    }
    return isChipEl(next) ? (next as HTMLSpanElement) : null;
  }

  function moveChipByKeyboard(chip: HTMLSpanElement, direction: -1 | 1): void {
    const el = editorRef.current;
    if (!el) return;
    const allChips = Array.from(el.querySelectorAll<HTMLSpanElement>(CHIP_SELECTOR));
    const focusedIdx = allChips.indexOf(chip);
    if (focusedIdx === -1) return;
    const targetIdx = focusedIdx + direction;
    if (targetIdx < 0 || targetIdx >= allChips.length) return;

    // Serialize to tokens, preserving text between chips
    type Token = { type: 'text'; value: string } | { type: 'chip'; name: string };
    const tokens: Token[] = [];
    for (const node of el.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent ?? '').replace(/\u200B/g, '');
        if (text) tokens.push({ type: 'text', value: text });
      } else if (node instanceof HTMLElement && node.dataset.formulaVar != null) {
        tokens.push({ type: 'chip', name: node.dataset.formulaVar });
      }
    }

    // Find the token indices for the two chips being swapped
    const chipTokenIdxs: number[] = [];
    tokens.forEach((t, i) => { if (t.type === 'chip') chipTokenIdxs.push(i); });
    const tokA = chipTokenIdxs[focusedIdx];
    const tokB = chipTokenIdxs[targetIdx];
    if (tokA === undefined || tokB === undefined) return;

    const tmp = tokens[tokA]!;
    tokens[tokA] = tokens[tokB]!;
    tokens[tokB] = tmp;

    const formula = tokens.map(t => t.type === 'chip' ? `{${t.name}}` : t.value).join('');
    // Every name here came from a chip that was already on screen, so it is known by construction.
    populate(el, formula, KNOWN(variablesRef.current), refNameRef.current);
    lastValueRef.current = formula;
    onChangeRef.current(formula);

    // Re-focus the chip that moved to targetIdx
    const newChips = Array.from(el.querySelectorAll<HTMLSpanElement>(CHIP_SELECTOR));
    newChips[targetIdx]?.focus();
  }

  function handleEditorMouseDown(e: React.MouseEvent) {
    const chip = (e.target as HTMLElement).closest<HTMLSpanElement>(CHIP_SELECTOR);
    if (!chip || !editorRef.current?.contains(chip)) return;
    e.preventDefault(); // prevent browser text selection / contenteditable cursor move

    /*
    * HIDDEN UNTIL THE DRAG ACTUALLY STARTS.
    *
    * It was appended visible here, at mousedown, which is before anything has moved — so a
    * CLICK put a copy of the chip on screen ten pixels to the right and took it away again
    * on release. Reported as the chip flashing to the right before the menu opens, which is
    * exactly what it was: the drag's ghost, for a drag that never happened.
    *
    * Built here rather than on first movement because the element is the thing `dragRef`
    * holds; `onMove` reveals it when the threshold is crossed.
    */
    const ghost = document.createElement('div');
    ghost.style.cssText = `position:fixed;pointer-events:none;z-index:10000;display:none;left:${e.clientX + 10}px;top:${e.clientY - 12}px;`;
    // Clone chip HTML to preserve its styling
    const ghostChip = chip.cloneNode(true) as HTMLSpanElement;
    ghostChip.style.cursor = 'grabbing';
    ghostChip.style.opacity = '0.9';
    ghost.appendChild(ghostChip);
    document.body.appendChild(ghost);

    const indicator = document.createElement('div');
    indicator.style.cssText = 'position:fixed;pointer-events:none;z-index:10000;width:3px;background:var(--color-ws-primary);border-radius:1px;box-shadow:0 0 0 2px color-mix(in srgb,var(--color-ws-primary) 35%,transparent);display:none;';
    document.body.appendChild(indicator);

    dragRef.current = {
      chip,
      ghost,
      indicator,
      isDragging: false,
      startX: e.clientX,
      startY: e.clientY,
    };
  }

  /** Opens the picker at the caret — the `{` shortcut's way in. */
  const insertPickerRef = useRef<PickerHandle | null>(null);
  /*
   * NEITHER CHARACTER COMES BACK on a dismissal, in an equation field.
   *
   * `[` never did: a lone bracket does not parse — `formulaParses('[')` is false — and the
   * bracket syntax that DOES parse is mathjs's matrices, which this app evaluates to nothing.
   * Putting it back would drop a syntax error into the field.
   *
   * `{` used to, on the argument that a brace is the field's own variable syntax and
   * hand-typing `{name}` had to keep working. It does not keep working: this field has no
   * literal-brace path at all — a variable is a CHIP here, the standing rule is that a
   * `{name}` is never literal text, and `insertVariable` is the only thing that makes one. So
   * the brace left behind was a character the field could not use either, and declining a
   * menu is not a request to type punctuation. Both shortcuts now leave the field as it was.
   *
   * This is an EQUATION-field rule. In prose both characters are ordinary text and both go
   * back, which is why the rich-text editor keeps its own behaviour.
   */
  const dismissCharRef = useRef('');
  /**
   * Which half of the insert menu is showing.
   *
   * State, not a ref: the menu is rendered, so a ref mutated on the keystroke that opens it
   * would not be read until some later render decided to happen — and a ref changed during
   * render survives a render React throws away. Reset to `both` on dismissal so the BUTTON,
   * which is undirected, is never left showing one typed character's half.
   */
  const [menuShow, setMenuShow] = useState<'both' | 'variables' | 'refs'>('both');

  /**
   * Put a literal character back where the caret was.
   *
   * Only `{` uses this, and only on a dismissal: the shortcut swallows the brace to open the
   * menu, so declining has to leave behind exactly what would have been typed. It restores
   * the remembered range first, for the reason `insertVariable` spells out at length — the
   * menu takes focus, and focusing a contenteditable collapses its selection to the start.
   */
  /**
   * Put the caret back where it was, without putting anything in.
   *
   * Dismissing the menu has to land the teacher back in the equation they were typing. The
   * trigger is a button beside the field, and focusing it means reaching for the mouse to get
   * back into the one line that was being written — so the field claims focus itself.
   *
   * Same restoration `insertRawText` opens with: `el.focus()` on a contenteditable collapses
   * the selection to the START, so the remembered range has to be put back explicitly or the
   * caret jumps to the front of the formula.
   */
  function focusEditor() {
    const el = editorRef.current;
    if (!el) return;
    const sel = window.getSelection();
    const saved = caretRef.current;
    el.focus();
    if (!sel) return;
    const target = document.createRange();
    if (saved && el.contains(saved.commonAncestorContainer)) {
      target.setStart(saved.startContainer, saved.startOffset);
      target.setEnd(saved.endContainer, saved.endOffset);
    } else {
      target.selectNodeContents(el);
      target.collapse(false);
    }
    sel.removeAllRanges();
    sel.addRange(target);
  }

  function insertRawText(text: string) {
    const el = editorRef.current;
    if (!el) return;
    const sel = window.getSelection();
    const saved = caretRef.current;
    el.focus();
    if (sel) {
      const target = document.createRange();
      if (saved && el.contains(saved.commonAncestorContainer)) {
        target.setStart(saved.startContainer, saved.startOffset);
        target.setEnd(saved.endContainer, saved.endOffset);
      } else {
        target.selectNodeContents(el);
        target.collapse(false);
      }
      sel.removeAllRanges();
      sel.addRange(target);
      const range = sel.getRangeAt(0);
      const node = document.createTextNode(text);
      range.deleteContents();
      range.insertNode(node);
      const after = document.createRange();
      after.setStart(node, node.length);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
    }
    const out = serialize(el);
    lastValueRef.current = out;
    onChange(out);
  }

  /**
   * The same insertion as a variable's, with the chip built from an ALIAS.
   *
   * `ensureAlias` is what turns an address into a name: pick the same answer twice and the
   * second chip joins the first's alias rather than starting a rival name for it. That is the
   * same call the rich-text picker makes, so a reference written in prose and one written in a
   * formula are the same reference.
   */
  function insertRef(ref: { taskId: string; blockId: string; facet: string }) {
    if (!refScope) return;
    const aliasId = refScope.ensureAlias(ref);
    // The alias was created through state, so this render's array does not hold it yet — the
    // same lag the rich-text picker ran into. The address is always in hand, so the label comes
    // from there when the lookup misses.
    const existing = refScope.aliases.find(a => a.id === aliasId);
    // The SHORT form: an equation box is one line, and the full address fills it — see
    // `aliasShortName`. A brand-new alias has no name yet, so its short form is the block
    // and facet, which is what `answerRefDefaultName` gives with the task omitted.
    const label = existing
      ? aliasShortName(existing, refScope, t, 'number')
      : answerRefDefaultName(refScope, ref, t, { omitTask: true, kinds: 'number' });
    insertChipNode(makeRefChip(aliasId, label, answerRefAddress(refScope, ref, t)));
  }

  /**
   * Put a chip where the caret was, whatever kind of chip it is.
   *
   * Extracted when recalls arrived rather than copied: the caret restoration below is the
   * subtle half, and two copies of it would be two chances to get the one bug it exists to
   * prevent.
   */
  function insertChipNode(chip: HTMLSpanElement) {
    const el = editorRef.current;
    if (!el) return;

    /*
     * PUT THE CARET BACK BEFORE FOCUSING, or the chip lands at the front of the formula.
     *
     * `el.focus()` on a contenteditable collapses its selection to the START. This function read
     * the selection immediately after focusing, so it always read position 0 and inserted there:
     * typing `sin(34)+` and picking `{kayak}` gave `{kayak}sin(34)+`.
     *
     * Opening the picker is what loses the caret in the first place — it is a real menu with a
     * search box, so focus has genuinely left the field by the time this runs. Restoring the
     * remembered range is the only way back to where the teacher actually was.
     */
    const sel = window.getSelection();
    const saved = caretRef.current;
    el.focus();
    if (sel) {
      const target = document.createRange();
      if (saved && el.contains(saved.commonAncestorContainer)) {
        target.setStart(saved.startContainer, saved.startOffset);
        target.setEnd(saved.endContainer, saved.endOffset);
      } else {
        // Never typed in, so there is no caret to honour. The END of the formula is the only
        // sensible place — you are adding to what is there, not prefixing it — and it is what
        // focus alone does NOT do: focus collapses to the start.
        target.selectNodeContents(el);
        target.collapse(false);
      }
      sel.removeAllRanges();
      sel.addRange(target);
    }

    const anchor = document.createTextNode(ZWS);

    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        range.insertNode(anchor);
        range.insertNode(chip);
      } else {
        el.appendChild(chip);
        el.appendChild(anchor);
      }
    } else {
      el.appendChild(chip);
      el.appendChild(anchor);
    }

    requestAnimationFrame(() => {
      const sel2 = window.getSelection();
      if (!sel2) return;
      const r = document.createRange();
      r.setStart(anchor, anchor.length);
      r.collapse(true);
      sel2.removeAllRanges();
      sel2.addRange(r);
    });

    const s = serialize(el);
    lastValueRef.current = s;
    onChange(s);
  }

  function insertVariable(name: string) { insertChipNode(makeChip(name)); }

  // ── Render ──────────────────────────────────────────────────────────────────

  /**
   * With nothing that can be a CHIP, this is a plain input.
   *
   * It used to ask only about variables, and that was right until recall existed. A recall is a
   * chip too — it needs the same contenteditable to render, the same caret handling, the same
   * picker — so a teacher with Answer Recall on and Variables off got a plain input and could
   * not put a recall in an answer equation at all. Reported as exactly that.
   *
   * Worse quietly: a field that ALREADY held one rendered its `[ara_…]` token as raw text in the
   * input, because a plain input has nothing to draw a chip with.
   *
   * Everything the contenteditable exists for — rendering chips, placing the caret around them,
   * offering the picker — needs chips. Without any, all it does is cost: no
   * `inputMode='decimal'`, so a teacher entering an answer on a phone gets the full keyboard
   * instead of the numeric keypad, and a `role='textbox'` div where a real input would do.
   *
   * A SHAPE, not another family. This is the same field either way — same `data-field-kind`, same
   * tint, same value, same evaluation — and what changes is only whether a variable can be put in
   * it, which is a fact about the TASK rather than about the field. Numerical had this right and
   * expressed it as `hasVariables ? FormulaField : TextField`, five times; carrying the ternary to
   * every other block would have been the wheel-reinvention this sweep exists to stop, so it moves
   * in here instead and those five collapse.
   *
   * Note the eval preview stays. `2*(3+4)` is still worth showing as 14, and `2*sin(30)` still
   * depends on `angleMode`, neither of which has anything to do with variables.
   */
  /*
   * THE ANGLE TOGGLE, defined once and rendered in BOTH branches below.
   *
   * It was only in the chip branch, so a field on a task with no variables — which renders as a
   * plain input — had no way to set its mode at all. The two branches differ in whether a chip
   * can go in the field, which is a fact about the task; the angle mode is a fact about the
   * formula, and `2*sin(30)` needs it either way.
   *
   * ONLY WHEN THIS FIELD ACTUALLY CALLS TRIG, and it goes again if the call is deleted: the mode
   * means nothing without one, and a control that is always there is a control nobody reads.
   */
  const angleToggle = changeAngleMode && TRIG_CALL_PATTERN.test(value) ? (
    <button
      type='button'
      /* Tagged with the id an undo of it NAMES. Pressing this writes `angleModes[<key>]`, so the op
         is called `angleModes.<key>` — and with no tag the reveal ladder found nothing and fell
         back to flashing the whole block for a press the teacher had just made. Nested inside the
         formula field's own `fieldId` tag, which names the equation rather than its unit. */
      data-field-id={`angleModes.${angleModeKey ?? fieldId}`}
      onClick={() => changeAngleMode(effectiveAngleMode === 'degrees' ? 'radians' : 'degrees')}
      aria-label={t(effectiveAngleMode === 'degrees'
        ? 'blocks.angleMode.toggleFromDegrees' : 'blocks.angleMode.toggleFromRadians')}
      title={t(effectiveAngleMode === 'degrees'
        ? 'blocks.angleMode.toggleFromDegrees' : 'blocks.angleMode.toggleFromRadians')}
      className='flex shrink-0 items-center self-stretch border-l border-ws-border bg-ws-btn px-2 text-[10px] font-semibold uppercase tracking-wide text-ws-text transition-colors hover:bg-ws-hover'
    >
      {t(effectiveAngleMode === 'degrees' ? 'blocks.angleMode.degShort' : 'blocks.angleMode.radShort')}
    </button>
  ) : null;

  /*
   * FOUR reasons to be chip-capable, and any one is enough. Two are "a chip could be put here"
   * and two are "a chip is already here" — the second pair matters because a field whose chip
   * can no longer be INSERTED must still be able to SHOW the one it has, or the token leaks.
   */
  const chipsPossible = namedVars.length > 0
    || (!refsOff && !!refScope)
    || hasVariableChip(value)
    || hasRecallChip(value);

  if (!chipsPossible) {
    return (
      <div data-field-id={fieldId} className={`flex flex-col gap-0.5 ${className}`}>
        <div className={`flex items-stretch overflow-hidden rounded border ${borderCls} transition-colors`}>
          <input
            type='text'
            inputMode='decimal'
            data-field-kind='formula'
            value={value}
            onChange={e => onChange(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            ref={el => { if (el && focusOnMount) el.focus(); }}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={showError || undefined}
            placeholder={placeholder}
            className='min-w-0 flex-1 bg-ws-btn px-3 py-3 text-sm text-ws-text outline-none placeholder:text-xs placeholder:italic placeholder:text-ws-muted'
          />
          {suffix != null && (
            <span aria-hidden='true' className='flex shrink-0 items-center bg-ws-btn pr-2 text-xs text-ws-muted'>{suffix}</span>
          )}
          {angleToggle}
        </div>
        {!hideEval && evalResult !== null && (
          evalResult instanceof Error
            ? <p className='text-xs text-ws-error-fg'>{t('blocks.formulaField.error')}</p>
            : <p className='text-xs text-ws-muted'>= {evalResult}</p>
        )}
      </div>
    );
  }

  return (
    <div data-field-id={fieldId} className={`flex flex-col gap-0.5 ${className}`}>
      {/* Input row */}
      <div className={`relative flex items-stretch overflow-hidden rounded border ${borderCls} transition-colors`}>
        {/* Ghost text as a SIBLING, not a ::before on the editable — the same shape RichTextEditor
            uses. A pseudo-element lives inside the contenteditable, so the browser has to decide
            where the caret goes relative to it: it landed after the hint, and once the hint was
            taken out of flow the caret jumped between the hint's start and end as the element
            re-rendered. Nothing inside the editable means nothing for the caret to negotiate with.
            `leading-5` matches text-sm's line box despite the smaller font, so the hint sits on
            exactly the line the first typed character will occupy — both start at top-3. */}
        {!value.trim() && placeholder && (
          <p aria-hidden='true' className='pointer-events-none absolute left-3 top-3 z-[1] select-none whitespace-nowrap text-xs italic leading-5 text-ws-muted'>
            {placeholder}
          </p>
        )}
        <div
          ref={editorRef}
          role='textbox'
          contentEditable
          suppressContentEditableWarning
          data-field-kind='formula'
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-multiline='false'
          aria-invalid={showError}
          data-placeholder={placeholder}
          onMouseDown={handleEditorMouseDown}
          onCopy={e => {
            /*
             * Hand over the FORMULA, not the rendering of it. The browser would give a faithful
             * copy of what is on screen, which carries the zero-width spacers that sit either
             * side of every chip; serialising instead means what lands on the clipboard is
             * exactly what would have been stored, and pastes back as itself.
             */
            const el = editorRef.current;
            const sel = window.getSelection();
            if (!el || !sel || sel.rangeCount === 0 || sel.isCollapsed) return;
            const range = sel.getRangeAt(0);
            if (!el.contains(range.commonAncestorContainer)) return;
            const holder = document.createElement('div');
            holder.appendChild(range.cloneContents());
            /*
             * TWO FLAVOURS, because two different readers take this.
             *
             * The plain one is for people and for the fields that hold no chips — Keyword and
             * Fill Blank among them — so it reads `[Kayak Velocity]`, not the uuid the
             * reference is stored as.
             *
             * The html one is for us, and carries the canonical formula so an equation pasted
             * into another equation is still the same equation. A NAME could not do that job:
             * names are not unique — this experience has two aliases called "slider low" —
             * and they change under rename, so reading one back is a guess that is wrong
             * quietly.
             *
             * One attribute on one empty span, rather than markup to be trusted. The paste
             * side reads that attribute and nothing else, so this stays as closed to arriving
             * html as the comment below says it is.
             */
            const canonical = serialize(holder);
            e.clipboardData.setData('text/plain', serializeReadable(holder));
            e.clipboardData.setData(
              'text/html',
              `<span ${FORMULA_CLIPBOARD_ATTR}="${canonical.replace(/"/g, '&quot;')}"></span>`,
            );
            e.preventDefault();
          }}
          onInput={handleInput}
          onPaste={e => {
            /*
             * Plain text only: a formula field has no use for pasted markup, and letting HTML in
             * is how a chip could arrive without passing through the variables menu.
             *
             * REBUILT, not spliced. This used to insert the text and call `handleInput`, whose
             * `autoChipBeforeCaret` matches `/\{([^{}]+)\}$/` — anchored at the caret, so pasting
             * `{river}+{kayak}` chipped `kayak` and left `river` as three words of literal text.
             * Copying a formula to reuse it across versions, which is the whole reason to paste
             * one, produced something that looked almost right and graded nothing.
             *
             * Going through `populate` means every `{name}` in the pasted run becomes a chip by
             * the same rule as every other way one is made, including the rule that an unknown
             * name stays as text.
             */
            e.preventDefault();
            const el = editorRef.current;
            /*
             * OUR OWN COPY FIRST, so an equation pasted into an equation is still the same
             * equation. The plain flavour reads `[Kayak Velocity]` now — right for a person
             * and for a field that holds no chips, and useless for rebuilding a reference,
             * because a name is neither unique nor stable. The copy side puts the canonical
             * formula in one attribute for exactly this.
             *
             * Still not "letting HTML in": one attribute is read, by name, and its contents
             * go through `populate` like any other text. Markup that is not ours is ignored
             * and the plain flavour is used, which is what used to happen to all of it.
             */
            const html = e.clipboardData.getData('text/html');
            const ours = html
              ? new DOMParser().parseFromString(html, 'text/html')
                .querySelector(`[${FORMULA_CLIPBOARD_ATTR}]`)?.getAttribute(FORMULA_CLIPBOARD_ATTR)
              : null;
            const text = (ours ?? e.clipboardData.getData('text/plain')).replace(/[\r\n]+/g, ' ');
            if (!el || !text) return;
            const sel = window.getSelection();
            const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
            if (!range || !el.contains(range.startContainer)) return;

            const [before, after] = serializeAround(el, range);
            populate(el, before + text + CARET_MARK + after, KNOWN(variables), refName);
            placeCaretAtMark(el);
            const value = serialize(el);
            lastValueRef.current = value;
            onChange(value);
          }}
          onFocus={() => { onFocus?.(); }}
          onBlur={() => { onBlur?.(); }}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); return; }
            /*
             * `{` OPENS THE PICKER — and here it is not a new meaning at all. In a formula
             * field a brace already starts a variable, so the shortcut is the character
             * finally doing what it has always said. Escape puts it back as a literal, which
             * is what a teacher hand-typing `{name}` would want.
             */
            if (e.key === '{' && !e.metaKey && !e.ctrlKey && !e.altKey && insertableVars.length > 0) {
              e.preventDefault();
              const sel = window.getSelection();
              const at = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).getBoundingClientRect() : null;
              const fallback = editorRef.current?.getBoundingClientRect();
              const rect = at && (at.width || at.height || at.top) ? at : fallback;
              dismissCharRef.current = '';
              /* A typed brace is a request for a VARIABLE, so the menu opens on the variables
                 alone. The button stays undirected and shows both — see `FormulaInsertMenu`. */
              setMenuShow('variables');
              if (rect) insertPickerRef.current?.openAt(rect);
              return;
            }
            /*
             * `[` OPENS THE RECALL PICKER, the same bargain one line up. The bracket is the
             * signifier everywhere else this feature appears — the button's icon, the chip's
             * own `renderText`, the rich-text shortcut — so typing it is the thing a teacher
             * who has met it once will try. Nothing to offer and no mounted picker both let
             * the bracket through: a shortcut that eats a keystroke and shows nothing is worse
             * than none, which is the rule the rich-text editor already states.
             */
            if (e.key === '[' && !e.metaKey && !e.ctrlKey && !e.altKey && refScope) {
              const sel = window.getSelection();
              const at = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).getBoundingClientRect() : null;
              const fallback = editorRef.current?.getBoundingClientRect();
              const rect = at && (at.width || at.height || at.top) ? at : fallback;
              if (!rect || !insertPickerRef.current) return;
              e.preventDefault();
              dismissCharRef.current = '';
              /* and a typed bracket is a request for an ANSWER, for the same reason. Being
                 shown the variables first here read as the menu ignoring what was typed. */
              setMenuShow('refs');
              insertPickerRef.current.openAt(rect);
              return;
            }
            const el = editorRef.current;
            if (!el) return;
            const target = e.target as HTMLElement;
            const chip = target.closest<HTMLSpanElement>(CHIP_SELECTOR);

            if (chip && el.contains(chip)) {
              if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                removeChip(chip);
              } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                moveChipByKeyboard(chip, e.key === 'ArrowLeft' ? -1 : 1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const chips = Array.from(el.querySelectorAll<HTMLSpanElement>(CHIP_SELECTOR));
                const idx = chips.indexOf(chip);
                const peer = chips[e.key === 'ArrowLeft' ? idx - 1 : idx + 1];
                if (peer) peer.focus(); else el.focus();
              }
              return;
            }

            // Not focused on a chip — the caret is a normal text cursor. Still check whether
            // it's sitting right at a chip's edge (see chipBeforeCaret/chipAfterCaret) so
            // Backspace/Delete remove the chip in one press instead of eating its invisible
            // ZWS anchor first and leaving the chip behind.
            if (e.key === 'Backspace') {
              const adjacent = chipBeforeCaret(el);
              if (adjacent) { e.preventDefault(); removeChip(adjacent); }
            } else if (e.key === 'Delete') {
              const adjacent = chipAfterCaret(el);
              if (adjacent) { e.preventDefault(); removeChip(adjacent); }
            }
          }}
          // min-h holds the box open when it is empty: the ghost text is a sibling and contributes
          // no height, so without this, clearing the field collapsed it to its own padding. 2.75rem
          // is py-3 (1.5rem) plus one text-sm line (1.25rem) — exactly a filled single-line field,
          // so nothing shifts as the first character is typed.
          className='relative min-h-[2.75rem] min-w-0 flex-1 overflow-x-auto bg-ws-btn px-3 py-3 text-sm text-ws-text focus:outline-none'
        />
        {suffix != null && (
          <span aria-hidden='true' className='flex shrink-0 items-center bg-ws-btn pr-2 text-xs text-ws-muted'>{suffix}</span>
        )}
        {angleToggle}

        {/* Portalled, so where it sits in the tree is only about which field owns it. */}
        {renaming && (
          <RecallRenamePopover
            aliasId={renaming.aliasId}
            anchor={renaming.anchor}
            onClose={() => setRenaming(null)}
          />
        )}

        {/* GROUPED AND SEARCHABLE, shared with the rich-text toolbar. The flat wrap of every
            chip that used to live here was fine at four variables and unreadable at forty,
            which one set of five stars makes ordinary. */}
        {/*
          * ONE BUTTON FOR BOTH, because this field has room for one — see `FormulaInsertMenu`.
          * `{` and `[` both open it; the menu's own groups say which is which, and the key
          * that was pressed is already the thing a teacher was reaching for.
          */}
        <FormulaInsertMenu
          variables={insertableVars}
          onVariable={insertVariable}
          onRef={insertRef}
          handleRef={insertPickerRef}
          /* The menu reads the recall scope from context ITSELF, so gating `refScope` here
             does not reach it — forcing the variables half is what actually closes that
             door. Found by the test, not by reading: the two look equivalent. */
          show={refsOff ? 'variables' : menuShow}
          refsWithheld={refsOff}
          /* The mirror, and the reason the glyph needed a third state: below the Variables rung
             this field still recalls an earlier answer, so the menu stays — but it is a `[]`
             menu now, not a `{]` one. `varsAllowed` already carries the grandfather clause, so
             a field holding a chip keeps its brace. */
          varsWithheld={!varsAllowed}
          /* Dismissed without choosing: nothing goes back — see `dismissCharRef`. Kept as a
             hook rather than deleted because the rich-text field shares this component and
             does put its character back. */
          onDismiss={() => { setMenuShow('both'); if (dismissCharRef.current) insertRawText(dismissCharRef.current); }}
          returnFocus={focusEditor}
        />
      </div>

      {/* Evaluated preview — suppressed by hideEval */}
      {!hideEval && evalResult !== null && (
        evalResult instanceof Error
          ? <p className='text-xs text-ws-error-fg'>{t('blocks.formulaField.error')}</p>
          : <p className='text-xs text-ws-muted'>= {evalResult}</p>
      )}

    </div>
  );
}

// Renders a formula string with {var} references highlighted as chips. Once rolled,
// each chip leads with the variable name as a tiny superscript and shows the resolved
// *value* at normal size — the value is what you actually need to sanity-check the
// arithmetic (e.g. "1 + [ᵛᵃʳ¹9.2] = 10.2" reads straight off the screen as "1+9.2=10.2"),
// while the superscript still traces it back to the variable without an inline "=" —
// embedding "name = value" as plain equal-size text reads as a nested equation
// (e.g. "1+(var1 = 9.2)=10.2"), which is what this replaced. Shared across every block whose
// formula strings use this same `{name}` token syntax (Numerical/GPS/Angle's answer formulas,
// Dropdown's branch equation/threshold) rather than duplicated per block.
export function FormulaWithValues({ formula, rolledValues }: { formula: string; rolledValues: Record<string, string> }) {
  const { t } = useTranslation();
  const scope = useAnswerRefScope();
  const parts: React.ReactNode[] = [];
  /*
   * BOTH KINDS OF CHIP, in one pass.
   *
   * This read `{name}` only, so a recalled answer travelled through as the literal text
   * `[ara_279668a3-…]` — and the answer bar, which is where a teacher checks what their setup
   * covers, showed a uuid. The two are the same kind of thing here: a name at rest, a number
   * once there is a roll, and the surface decides which by passing one or not.
   */
  const re = /\{([^}]+)\}|\[(ara_[0-9a-zA-Z-]+)\]/g;
  let last = 0; let m; let i = 0;
  while ((m = re.exec(formula)) !== null) {
    if (m.index > last) parts.push(formula.slice(last, m.index));
    const aliasId = m[2];
    if (aliasId !== undefined) {
      const alias = scope?.aliases.find(a => a.id === aliasId);
      // An alias that is gone keeps its token as text, exactly as an unknown variable keeps its
      // braces: a chip naming nothing is a lie a teacher cannot see through.
      const label = alias ? aliasShortName(alias, scope!, t, 'number') : null;
      const val = rolledValues[aliasId];
      /*
       * THE RECALL CHIP'S OWN LOOK, not the variable one.
       *
       * `ws-var-chip` was the lazy reuse and it read as a variable: the same answer appeared
       * teal here and in recall blue three lines up in the equation field, so one thing looked
       * like two. `STATUS_CLASS` also carries the conditional and broken treatments, which a
       * variable chip has no equivalent for — and those are exactly what a teacher needs to
       * see on a bar after rearranging blocks, which is when a recall goes out of reach.
       */
      const chipClass = alias && scope
        ? STATUS_CLASS[aliasStatus(alias, scope.currentTaskId, scope.tasks)]
        : STATUS_CLASS.broken;
      parts.push(label === null ? `[${aliasId}]` : (
        <span key={i++} className={`${chipClass} gap-0.5`}>
          {val != null ? (
            <>
              <sup className='text-[8px] font-normal leading-none opacity-70'>{label}</sup>
              {val}
            </>
          ) : label}
        </span>
      ));
      last = re.lastIndex;
      continue;
    }
    const name = m[1]!;
    const val = rolledValues[name];
    parts.push(
      <span key={i++} className='ws-var-chip gap-0.5'>
        {val != null ? (
          <>
            <sup className='text-[8px] font-normal leading-none text-ws-primary/70 dark:text-ws-text-link/70'>{name}</sup>
            {val}
          </>
        ) : `{${name}}`}
      </span>
    );
    last = re.lastIndex;
  }
  if (last < formula.length) parts.push(formula.slice(last));
  return <>{parts}</>;
}

/**
 * Draws the formula parts of an already-translated summary as CHIPS.
 *
 * A targeted trigger stores its bounds raw (`range:{n}*10:{n}*20`), and every block's summary
 * interpolates those bounds verbatim into a translated sentence — so the formula text appears in the
 * output exactly as authored, and can be found and replaced without touching i18n. Translating
 * first and substituting after is what keeps the sentence's word order intact in every locale; the
 * alternative, assembling the sentence from fragments, is how a translation gets hard-coded to
 * English grammar.
 *
 * Values are matched longest-first: `{n}` is a substring of `{n}*10`, and matching the short one
 * first would chip the variable and leave `*10` stranded as text.
 *
 * `rolledValues` decides the STYLE, per the two-style rule. A feedback trigger names the condition,
 * so it passes nothing and the variable stays unrolled (`{n}`). The testing panel passes the roll it
 * is testing, and the same chip renders as name-plus-value — because that surface exists precisely
 * to show what the variables resolved to.
 */
export function chipifyFormulaValues(
  text: string, values: string[], rolledValues: Record<string, string> = {},
): React.ReactNode {
  const withChips = values.filter(v => v.trim() !== '' && v.includes('{') && text.includes(v));
  if (withChips.length === 0) return text;
  const ordered = [...new Set(withChips)].sort((a, b) => b.length - a.length);
  const escaped = ordered.map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'g'));
  return (
    <>
      {parts.map((p, i) => ordered.includes(p)
        ? <FormulaWithValues key={i} formula={p} rolledValues={rolledValues} />
        : <span key={i}>{p}</span>)}
    </>
  );
}

/**
 * Six significant figures — what `fmtLocale` prints every other number at.
 *
 * `resolveTriggerValues` stringifies with `String(n)`, which is right for the evaluator (it wants
 * the whole double) and wrong for a chip: `1/400` arrives as 0.0025000000000000005. ROUNDED, not
 * formatted, because the result goes back into a trigger string and a locale's thousands separator
 * would not survive being parsed out again.
 */
export function displayPrecision(n: number): number {
  return Number.isFinite(n) ? parseFloat(n.toPrecision(6)) : n;
}

/**
 * A value condition as a chip: the number it fires on, and — for a LITERAL equation — the
 * arithmetic behind it.
 *
 * There are exactly two readings, and the scenario picks:
 *   - no scenario: the condition as authored, variables as chips;
 *   - a scenario:  the resolved number, alone.
 *
 * `= 400 (398+2)` for a literal, because three conditions written `398+2`, `399+1` and `400` are
 * one condition three times and a teacher looking at three chips all reading `= 400` cannot tell
 * them apart. A VARIABLE gets no parenthetical: it resolves differently every roll and its text is
 * already on the same panel as chips whenever no scenario is picked, so repeating it beside the
 * number adds nothing and crowds a dense row.
 *
 * Shared because slider showed the one reading that helps nobody — substituting into the formula
 * and stopping, so a chip read `ᵗᵉˢᵗ400*5` and left the arithmetic to the reader, while the
 * accepted-answer chip beside it printed a finished number.
 */
export function resolvedTriggerChip(opts: {
  trigger: string;
  /** The roll to resolve against. Empty means no scenario: the condition stays symbolic. */
  scenario: Record<string, string>;
  /** The block's own sentence for a condition, given a (possibly resolved) trigger string. */
  summaryOf: (trigger: string) => string;
  /** The block's formula evaluator, already bound to its angle mode. */
  evaluate: (expr: string, values: Record<string, string>) => number;
  /** The raw bound values this trigger carries, in order. */
  valuesOf: (trigger: string) => string[];
  hasChip: (value: string) => boolean;
  resolveTrigger: (trigger: string, resolve: (v: string) => number) => string;
}): React.ReactNode {
  const { trigger, scenario, summaryOf, evaluate, valuesOf, hasChip, resolveTrigger } = opts;
  const live = Object.keys(scenario).length > 0;
  const resolved = resolveTrigger(trigger, val =>
    live ? displayPrecision(evaluate(val, scenario))
         : (hasChip(val) ? NaN : displayPrecision(evaluate(val, {}))));
  const summary = chipifyFormulaValues(summaryOf(resolved), valuesOf(trigger), scenario);
  const sources = valuesOf(trigger).filter((v, i) =>
    v.trim() !== '' && !hasChip(v) && v !== valuesOf(resolved)[i]);
  if (sources.length === 0) return summary;
  // Inherits the chip's own colour — a muted grey read as a separate, lesser annotation, when it is
  // part of the same statement: the condition and the equation behind it.
  return <>{summary} <span className='italic'>({sources.join(', ')})</span></>;
}
